import { useEffect, useState, type ReactNode } from 'react';
import { useRouter } from '@tanstack/react-router';
import {
  Building2,
  ChevronDown,
  Crown,
  FolderTree,
  LayoutDashboard,
  Layers,
  ListChecks,
  LogOut,
  ShieldCheck,
  Sprout,
  Target,
  Tractor,
  Factory,
  Ruler,
  Users,
  BookOpen,
  Wheat,
} from 'lucide-react';
import { BrandIcon } from '@/components/ui/brand-icon';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { captureException } from '@/lib/sentry';
import { ProfileDrawer } from '@/components/profile/ProfileDrawer';

/**
 * PlatformAdminLayout — root-tool layout для founder'а и platform-staff.
 * Отдельный от AppShell shell: top bar Crown + «ИИ-Колхоз · Platform Admin»,
 * левый sticky sidebar 240px (Platform / Справочники), main `max-w-7xl` с
 * 28×32 padding.
 *
 * Auth-guard: подгружает /api/v1/auth/me, если `user.platform_role` пустой —
 * 403 fallback. Внутри tenant'ов мы намеренно НЕ показываем эту страницу
 * (это internal-root tooling, не клиентская фича).
 *
 * Wireframe: docs/superpowers/prototypes/2026-06-07-ak190-platform-admin/
 */

interface PlatformMe {
  user: {
    email: string;
    display_name?: string;
    first_name?: string;
    platform_role?: 'superadmin' | 'admin' | 'support' | null;
  };
}

interface NavItem {
  key: string;
  label: string;
  icon: ReactNode;
  to: string;
  /** Визуальный indent для подпунктов раздела «Справочники». */
  sub?: boolean;
}

const NAV_SECTIONS: { title: string; items: NavItem[] }[] = [
  {
    title: 'Platform',
    items: [
      { key: 'dashboard', label: 'Dashboard', icon: <LayoutDashboard className="h-4 w-4" />, to: '/platform-admin' },
      { key: 'companies', label: 'Компании', icon: <Building2 className="h-4 w-4" />, to: '/platform-admin/companies' },
      { key: 'users', label: 'Пользователи', icon: <Users className="h-4 w-4" />, to: '/platform-admin/users' },
      { key: 'admins', label: 'Админы', icon: <ShieldCheck className="h-4 w-4" />, to: '/platform-admin/admins' },
    ],
  },
  {
    title: 'Справочники',
    items: [
      // Reference parent — заголовок раздела; кликабельный fallback на культуры.
      { key: 'reference', label: 'Reference', icon: <BookOpen className="h-4 w-4" />, to: '/platform-admin/reference/crops' },
      { key: 'ref-crops', label: 'Товарные культуры', icon: <Sprout className="h-3.5 w-3.5" />, to: '/platform-admin/reference/crops', sub: true },
      { key: 'ref-crop-queue', label: 'Разбор культур', icon: <ListChecks className="h-3.5 w-3.5" />, to: '/platform-admin/reference/crop-queue', sub: true },
      { key: 'ref-varieties', label: 'Сорта и гибриды', icon: <Wheat className="h-3.5 w-3.5" />, to: '/platform-admin/reference/varieties', sub: true },
      { key: 'ref-diseases', label: 'Болезни', icon: <BrandIcon name="disease" size={14} />, to: '/platform-admin/reference/diseases', sub: true },
      { key: 'ref-pests', label: 'Вредители', icon: <BrandIcon name="pest" size={14} />, to: '/platform-admin/reference/pests', sub: true },
      { key: 'ref-weeds', label: 'Сорняки', icon: <BrandIcon name="weed" size={14} />, to: '/platform-admin/reference/weeds', sub: true },
      { key: 'ref-operation-types', label: 'Тех. операции', icon: <Tractor className="h-3.5 w-3.5" />, to: '/platform-admin/reference/operation-types', sub: true },
      { key: 'ref-machinery', label: 'Техника', icon: <BrandIcon name="machine" size={14} />, to: '/platform-admin/reference/machinery', sub: true },
      { key: 'ref-pesticides', label: 'Пестициды (СЗР)', icon: <BrandIcon name="pharmacy" size={14} />, to: '/platform-admin/reference/pesticides', sub: true },
    ],
  },
  {
    title: 'Мини-справочники',
    items: [
      { key: 'ref-crop-categories', label: 'Товарные группы', icon: <Layers className="h-3.5 w-3.5" />, to: '/platform-admin/reference/crop-categories', sub: true },
      { key: 'ref-crop-groups', label: 'Биологические культуры', icon: <Sprout className="h-3.5 w-3.5" />, to: '/platform-admin/reference/crop-groups', sub: true },
      { key: 'ref-goals', label: 'Цели выращивания', icon: <Target className="h-3.5 w-3.5" />, to: '/platform-admin/reference/growing-goals', sub: true },
      { key: 'ref-field-states', label: 'Состояния поля', icon: <FolderTree className="h-3.5 w-3.5" />, to: '/platform-admin/reference/field-states', sub: true },
      { key: 'ref-land-categories', label: 'Типы угодий', icon: <FolderTree className="h-3.5 w-3.5" />, to: '/platform-admin/reference/land-categories', sub: true },
      { key: 'ref-disease-categories', label: 'Категории болезней', icon: <FolderTree className="h-3.5 w-3.5" />, to: '/platform-admin/reference/disease-categories', sub: true },
      { key: 'ref-pest-categories', label: 'Категории вредителей', icon: <FolderTree className="h-3.5 w-3.5" />, to: '/platform-admin/reference/pest-categories', sub: true },
      { key: 'ref-weed-categories', label: 'Категории сорняков', icon: <FolderTree className="h-3.5 w-3.5" />, to: '/platform-admin/reference/weed-categories', sub: true },
      { key: 'ref-weed-life-cycles', label: 'Жизненные циклы сорняков', icon: <FolderTree className="h-3.5 w-3.5" />, to: '/platform-admin/reference/weed-life-cycles', sub: true },
      { key: 'ref-machinery-types', label: 'Типы техники', icon: <FolderTree className="h-3.5 w-3.5" />, to: '/platform-admin/reference/machinery-types', sub: true },
      { key: 'ref-manufacturers', label: 'Производители', icon: <Factory className="h-3.5 w-3.5" />, to: '/platform-admin/reference/manufacturers', sub: true },
      { key: 'ref-units', label: 'Единицы измерения', icon: <Ruler className="h-3.5 w-3.5" />, to: '/platform-admin/reference/units', sub: true },
    ],
  },
];

export function PlatformAdminLayout({ children }: { children: ReactNode }) {
  const router = useRouter();
  const [me, setMe] = useState<PlatformMe | null>(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [authState, setAuthState] = useState<'loading' | 'authorized' | 'forbidden'>(
    'loading',
  );

  useEffect(() => {
    let cancelled = false;
    // Dev-only bypass для UI-предпросмотра без backend. Прод и stage
    // всегда ходят на /auth/me. Включается VITE_PLATFORM_ADMIN_DEV_BYPASS_AUTH=true.
    const devBypass =
      import.meta.env.DEV &&
      (import.meta.env.VITE_PLATFORM_ADMIN_DEV_BYPASS_AUTH as string | undefined) ===
        'true';
    if (devBypass) {
      setMe({
        user: {
          email: 'founder@aikolxoz.ru',
          display_name: 'Аркадий Сосновский',
          platform_role: 'superadmin',
        },
      });
      setAuthState('authorized');
      return () => {};
    }
    (async () => {
      try {
        const m = await api.get<PlatformMe>('/api/v1/auth/me');
        if (cancelled) return;
        setMe(m);
        setAuthState(m.user.platform_role ? 'authorized' : 'forbidden');
      } catch (err) {
        // api.ts уже редиректит на /login при 401 — здесь сюда не дойдёт.
        if (import.meta.env.DEV) console.error('[platform-admin] /auth/me failed', err);
        else captureException(err, { tags: { source: 'PlatformAdminLayout.authMe' } });
        if (!cancelled) setAuthState('forbidden');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const pathname = router.state.location.pathname;

  const navigate = (path: string) => router.navigate({ to: path });

  const handleLogout = async () => {
    try {
      await api.post('/api/v1/auth/logout');
    } finally {
      window.location.href = '/login';
    }
  };

  if (authState === 'loading') {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-background">
        <span className="text-sm text-muted-foreground">Загружаем…</span>
      </div>
    );
  }

  if (authState === 'forbidden') {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-background p-6">
        <div className="max-w-md rounded-lg border border-border bg-card p-8 text-center">
          <div className="mx-auto mb-3 inline-flex h-10 w-10 items-center justify-center rounded-md bg-destructive/10 text-destructive">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <h1 className="mb-1 text-lg font-semibold">403 · Доступ запрещён</h1>
          <p className="mb-4 text-sm text-muted-foreground">
            Platform Admin — internal-root tooling. Вход доступен только пользователям с
            platform_role (superadmin / admin / support).
          </p>
          <Button variant="outline" onClick={() => navigate('/dashboard')}>
            На рабочий стол
          </Button>
        </div>
      </div>
    );
  }

  const displayName =
    me?.user.display_name ?? me?.user.first_name ?? me?.user.email ?? '...';
  const avatarLetter = displayName[0]?.toUpperCase() ?? '?';
  const platformRole = me?.user.platform_role ?? 'admin';

  const isActive = (to: string) => {
    if (to === '/platform-admin') return pathname === '/platform-admin';
    // Reference parent — выделяем активным только если активна одна из подстраниц,
    // но НЕ когда пользователь на конкретной подстранице (там активен подпункт).
    if (to === '/platform-admin/reference/crops' && pathname === '/platform-admin/reference/crops') {
      return true;
    }
    return pathname === to || pathname.startsWith(`${to}/`);
  };

  return (
    // h-dvh — учитывает нижнюю адресную строку мобильного браузера (AK-374).
    <div className="flex h-dvh w-full overflow-hidden bg-background">
      {/* Left sidebar */}
      <aside className="hidden w-[240px] shrink-0 flex-col border-r border-border bg-card md:flex">
        <div className="flex h-14 items-center gap-2 border-b border-border px-4">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <Crown className="h-4 w-4" />
          </div>
          <div className="min-w-0 leading-tight">
            <div className="truncate text-sm font-semibold text-foreground">
              ИИ-Колхоз
            </div>
            <div className="truncate text-[11px] uppercase tracking-wide text-muted-foreground">
              Platform Admin
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-2 py-3">
          {NAV_SECTIONS.map((section) => (
            <div key={section.title} className="mb-4">
              <div className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {section.title}
              </div>
              <ul>
                {section.items.map((item) => {
                  const active = isActive(item.to);
                  return (
                    <li key={item.key}>
                      <button
                        type="button"
                        onClick={() => navigate(item.to)}
                        className={cn(
                          'flex w-full items-center gap-2.5 rounded-md text-sm transition-colors',
                          item.sub ? 'h-8 pl-7 pr-3 text-[13px]' : 'h-9 px-3',
                          active
                            ? 'bg-accent-brand/12 font-medium text-accent-brand'
                            : 'text-foreground hover:bg-accent',
                        )}
                      >
                        <span className={cn(active && 'text-accent-brand')}>
                          {item.icon}
                        </span>
                        <span className="truncate">{item.label}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>
      </aside>

      {/* Main column */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Top bar */}
        <header className="flex h-14 shrink-0 items-center justify-between border-b border-border bg-card px-6">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground md:hidden">
              <Crown className="h-4 w-4" />
            </div>
            <div className="leading-tight">
              <div className="text-sm font-semibold text-foreground">
                ИИ-Колхоз · Platform Admin
              </div>
              <div className="text-[11px] text-muted-foreground">
                {displayName} · {platformRole}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" aria-label="Уведомления">
              <BrandIcon name="nav24-navbar-notifications" size={16} />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-1.5 rounded-md px-1.5 py-1 hover:bg-accent transition-colors"
                  aria-label="Меню пользователя"
                >
                  <Avatar className="h-7 w-7">
                    <AvatarFallback className="bg-primary/10 text-primary text-[11px] font-semibold">
                      {avatarLetter}
                    </AvatarFallback>
                  </Avatar>
                  <ChevronDown className="h-3 w-3 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onSelect={() => navigate('/dashboard')}>
                  <Building2 className="h-4 w-4" />
                  Перейти в рабочий стол
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setProfileOpen(true)}>
                  <BrandIcon name="person" size={16} />
                  Профиль
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={handleLogout}>
                  <LogOut className="h-4 w-4" />
                  Выйти
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto bg-secondary/40">
          {/* h-full + flex-col let list pages fill remaining space (VirtualTable
              с height="100%") без viewport-констант; для обычных block-страниц
              поведение не меняется — content-sized children не растягиваются. */}
          <div className="mx-auto flex h-full max-w-7xl flex-col px-8 py-7">
            {children}
          </div>
        </main>
      </div>

      <ProfileDrawer open={profileOpen} onOpenChange={setProfileOpen} />
    </div>
  );
}

export default PlatformAdminLayout;
